import { useState, useMemo, useRef, useCallback } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { STATUS_MAP, isOverdue, todayStr, parseDate, toISODate, addDays, daysBetween } from "@/lib/taskConstants";
import { Button } from "@/components/ui/button";

const ZOOMS = [
  { value: "day", label: "Día", dayWidth: 48 },
  { value: "week", label: "Semana", dayWidth: 24 },
  { value: "month", label: "Mes", dayWidth: 10 },
];

const MONTHS_SHORT = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

export const GanttView = ({ tasks, subtasksByParent = {}, onSelect, onUpdateDates }) => {
  const [zoom, setZoom] = useState("week");
  const [drag, setDrag] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  const containerRef = useRef(null);
  const dayWidth = ZOOMS.find((z) => z.value === zoom).dayWidth;

  const planned = useMemo(() => tasks.filter((t) => t.start_date && t.due_date), [tasks]);
  const unplanned = tasks.length - planned.length;
  const today = todayStr();

  const toggleCollapse = (id) => setCollapsed((c) => ({ ...c, [id]: !c[id] }));

  const rows = useMemo(() => {
    const out = [];
    for (const t of planned) {
      out.push({ task: t, isSub: false });
      if (!collapsed[t.id]) {
        for (const s of subtasksByParent[t.id] || []) out.push({ task: s, isSub: true });
      }
    }
    return out;
  }, [planned, subtasksByParent, collapsed]);
  const rowIndexById = useMemo(() => Object.fromEntries(rows.map((r, i) => [r.task.id, i])), [rows]);

  const { rangeStart, totalDays, monthSpans } = useMemo(() => {
    let min = today, max = today;
    for (const { task: t } of rows) {
      if (t.start_date && t.start_date < min) min = t.start_date;
      if (t.due_date && t.due_date > max) max = t.due_date;
    }
    const start = addDays(min, -3);
    let days = daysBetween(start, addDays(max, 7)) + 1;
    if (days < 35) days = 35;
    const spans = [];
    let cursor = parseDate(start);
    let remaining = days;
    while (remaining > 0) {
      const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
      const dayOfMonth = cursor.getDate();
      const span = Math.min(daysInMonth - dayOfMonth + 1, remaining);
      spans.push({ label: `${MONTHS_SHORT[cursor.getMonth()]} ${cursor.getFullYear()}`, days: span });
      cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
      remaining -= span;
    }
    return { rangeStart: start, totalDays: days, monthSpans: spans };
  }, [rows, today]);

  const dayCells = useMemo(
    () => Array.from({ length: totalDays }, (_, i) => parseDate(addDays(rangeStart, i))),
    [rangeStart, totalDays]
  );

  const startDrag = useCallback((e, task, mode) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    let delta = 0;
    const onMove = (ev) => {
      delta = Math.round((ev.clientX - startX) / dayWidth);
      setDrag({ taskId: task.id, mode, delta });
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      setDrag(null);
      if (delta !== 0) {
        if (mode === "move") {
          onUpdateDates(task.id, { start_date: addDays(task.start_date, delta), due_date: addDays(task.due_date, delta) });
        } else if (mode === "resize-r") {
          const newDue = addDays(task.due_date, delta);
          if (newDue >= task.start_date) onUpdateDates(task.id, { due_date: newDue });
        } else if (mode === "resize-l") {
          const newStart = addDays(task.start_date, delta);
          if (newStart <= task.due_date) onUpdateDates(task.id, { start_date: newStart });
        }
      }
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }, [dayWidth, onUpdateDates]);

  const todayOffset = daysBetween(rangeStart, today);

  return (
    <div className="space-y-3" data-testid="gantt-view">
      <div className="flex items-center gap-2">
        <div className="flex rounded-lg border overflow-hidden">
          {ZOOMS.map((z) => (
            <button key={z.value} onClick={() => setZoom(z.value)}
              data-testid={`gantt-zoom-${z.value}`}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                zoom === z.value ? "bg-primary text-primary-foreground" : "hover:bg-secondary"
              }`}>
              {z.label}
            </button>
          ))}
        </div>
        {unplanned > 0 && (
          <span className="text-xs text-muted-foreground" data-testid="gantt-unplanned-count">
            {unplanned} {unplanned === 1 ? "tarea sin planificar" : "tareas sin planificar"} (requieren fecha de inicio y fin)
          </span>
        )}
      </div>

      {planned.length === 0 ? (
        <div className="rounded-xl border bg-card p-10 text-center">
          <p className="text-2xl mb-2">📊</p>
          <p className="text-sm text-muted-foreground">
            Ninguna tarea tiene fecha de inicio y fin.<br />Añade ambas fechas a una tarea para verla en el Gantt.
          </p>
        </div>
      ) : (
        <div className="rounded-xl border bg-card overflow-hidden">
          <div className="overflow-x-auto" ref={containerRef}>
            <div style={{ minWidth: 180 + totalDays * dayWidth }}>
              {/* Header months */}
              <div className="flex border-b bg-secondary/40 sticky top-0">
                <div className="w-[180px] shrink-0 border-r px-3 py-1.5 text-xs font-semibold text-muted-foreground">Tarea</div>
                <div className="flex">
                  {monthSpans.map((m) => (
                    <div key={m.label} style={{ width: m.days * dayWidth }}
                      className="text-xs font-semibold text-muted-foreground px-2 py-1.5 border-r truncate">
                      {m.label}
                    </div>
                  ))}
                </div>
              </div>
              {/* Header days */}
              {dayWidth >= 20 && (
                <div className="flex border-b">
                  <div className="w-[180px] shrink-0 border-r" />
                  <div className="flex">
                    {dayCells.map((d) => {
                      const iso = toISODate(d);
                      const weekend = d.getDay() === 0 || d.getDay() === 6;
                      return (
                        <div key={iso} style={{ width: dayWidth }}
                          className={`text-center text-[10px] py-1 border-r border-border/40 ${
                            iso === today ? "bg-primary/10 text-primary font-bold" : weekend ? "bg-secondary/40 text-muted-foreground" : "text-muted-foreground"
                          }`}>
                          {d.getDate()}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {/* Rows */}
              <div className="relative">
                <div className="absolute top-0 bottom-0 w-px bg-destructive z-10 pointer-events-none"
                  style={{ left: 180 + todayOffset * dayWidth + dayWidth / 2 }} data-testid="gantt-today-line" />
                <svg className="absolute inset-0 pointer-events-none z-[5]" data-testid="gantt-dependency-lines"
                  width={180 + totalDays * dayWidth} height={rows.length * 36}>
                  <defs>
                    <marker id="dep-arrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                      <path d="M0,0 L6,3 L0,6 z" fill="#5F6368" />
                    </marker>
                  </defs>
                  {planned.map((t) =>
                    (t.dependency_ids || []).map((depId) => {
                      const dep = planned.find((p) => p.id === depId);
                      if (!dep) return null;
                      const x1 = 180 + (daysBetween(rangeStart, dep.start_date) + daysBetween(dep.start_date, dep.due_date) + 1) * dayWidth;
                      const y1 = rowIndexById[dep.id] * 36 + 18;
                      const x2 = 180 + daysBetween(rangeStart, t.start_date) * dayWidth;
                      const y2 = rowIndexById[t.id] * 36 + 18;
                      return (
                        <path key={`${t.id}-${depId}`}
                          d={`M ${x1} ${y1} C ${x1 + 24} ${y1}, ${x2 - 24} ${y2}, ${Math.max(x2 - 2, x1 + 4)} ${y2}`}
                          fill="none" stroke="#5F6368" strokeWidth="1.5" strokeDasharray="4 3"
                          markerEnd="url(#dep-arrow)" opacity="0.7" />
                      );
                    })
                  )}
                </svg>
                {rows.map(({ task: t, isSub }) => {
                  const hasBar = !!(t.start_date && t.due_date);
                  const isDragging = drag?.taskId === t.id;
                  const delta = isDragging ? drag.delta : 0;
                  let startOff = hasBar ? daysBetween(rangeStart, t.start_date) : 0;
                  let duration = hasBar ? daysBetween(t.start_date, t.due_date) + 1 : 0;
                  if (isDragging) {
                    if (drag.mode === "move") startOff += delta;
                    else if (drag.mode === "resize-r") duration = Math.max(1, duration + delta);
                    else if (drag.mode === "resize-l") { startOff += delta; duration = Math.max(1, duration - delta); }
                  }
                  const color = isOverdue(t) ? "#D93025" : STATUS_MAP[t.status]?.color;
                  const subCount = !isSub ? (subtasksByParent[t.id] || []).length : 0;
                  return (
                    <div key={t.id}
                      className={`flex border-b border-border/50 hover:bg-secondary/30 group ${isSub ? "bg-card" : "bg-primary/[0.06]"}`}
                      data-testid={`gantt-row-${t.id}`}>
                      <div className={`w-[180px] shrink-0 border-r flex items-center ${isSub ? "pl-7 pr-3" : "pl-1 pr-3"}`}>
                        {subCount > 0 ? (
                          <button onClick={(e) => { e.stopPropagation(); toggleCollapse(t.id); }}
                            data-testid={`gantt-collapse-${t.id}`}
                            className="shrink-0 p-0.5 rounded text-muted-foreground hover:text-foreground hover:bg-secondary">
                            {collapsed[t.id] ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                          </button>
                        ) : !isSub ? (
                          <span className="w-[18px] shrink-0" />
                        ) : null}
                        <button onClick={() => onSelect(isSub ? t.parent_task_id : t.id)} data-testid={`gantt-row-label-${t.id}`}
                          className={`flex-1 min-w-0 py-2.5 text-xs text-left truncate hover:text-primary ${
                            isSub ? "text-muted-foreground" : "font-medium"}`}>
                          {isSub ? <span className="text-muted-foreground/60 mr-1">└</span> : null}
                          {t.title}
                          {subCount > 0 && collapsed[t.id] && (
                            <span className="ml-1 text-[10px] text-muted-foreground font-normal">(+{subCount})</span>
                          )}
                        </button>
                      </div>
                      <div className="relative flex-1 h-9" style={{
                        width: totalDays * dayWidth,
                        backgroundImage: `repeating-linear-gradient(to right, hsl(var(--border) / 0.55) 0px, hsl(var(--border) / 0.55) 1px, transparent 1px, transparent ${dayWidth}px)`,
                      }}>
                        {hasBar && (
                        <div
                          className={`absolute rounded-md flex items-center group/bar select-none ${isSub ? "top-2.5 h-4" : "top-1.5 h-6"} ${isDragging ? "opacity-80 z-20" : ""}`}
                          style={{ left: startOff * dayWidth, width: Math.max(duration * dayWidth - 2, dayWidth - 2), backgroundColor: color, cursor: "grab", opacity: isSub && !isDragging ? 0.85 : undefined }}
                          onPointerDown={(e) => startDrag(e, t, "move")}
                          onClick={(e) => { if (!drag) { e.stopPropagation(); onSelect(isSub ? t.parent_task_id : t.id); } }}
                          data-testid={`gantt-bar-${t.id}`}
                        >
                          <span onPointerDown={(e) => startDrag(e, t, "resize-l")}
                            className="w-2 h-full cursor-ew-resize shrink-0 opacity-0 group-hover/bar:opacity-100 bg-black/20 rounded-l-md" />
                          <span className="text-[10px] text-white font-medium truncate px-1 flex-1 pointer-events-none">
                            {t.title}
                          </span>
                          <span onPointerDown={(e) => startDrag(e, t, "resize-r")}
                            className="w-2 h-full cursor-ew-resize shrink-0 opacity-0 group-hover/bar:opacity-100 bg-black/20 rounded-r-md" />
                        </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4 px-3 py-2 border-t bg-secondary/30 flex-wrap">
            {[["#9AA0A6", "Pendiente"], ["#1A73E8", "En progreso"], ["#9D4CDD", "En revisión"], ["#1E8E3E", "Completada"], ["#D93025", "Vencida"]].map(([c, l]) => (
              <span key={l} className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: c }} /> {l}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
